/* MolCellQuest — 「間違えた問題」の記録と解き直し
   ──────────────────────────────────────────────────────────
   「あとで解き直す」(review.svelte.js) との違い：
     review … 自分で印を付ける。意識的に選んだ問題。
     missed … 間違えると自動で記録される。選ぶ必要がない。
   どちらもアカウント登録者限定。クラウドに同期されて初めて
   「試験前に別の端末で開く」という使い方ができるようになるため。

   progress.missed は problemId -> { n, w, c }。
     n … 誤答回数（累計）
     w … 最後に間違えた時刻
     c … 最後に克服した（間違えたあと正解した）時刻
   「いま解き直すべき問題」は w > c。克服しても記録は消さず、
   もう一度間違えれば w が更新されてまた対象に戻る。
   ────────────────────────────────────────────────────────── */

import { progress, save } from "./state.svelte.js";
import { auth } from "./authState.svelte.js";
import { CURRICULUM, problemById } from "../../data/problems/index.js";

/** この機能を使えるか */
export function canUseMissed() {
  return !!auth.user;
}

function entry(id) {
  const e = progress.missed?.[id];
  if (!e || typeof e !== "object") return null;
  return { n: Number(e.n) || 0, w: Number(e.w) || 0, c: Number(e.c) || 0 };
}

/** 記録があるか（克服済みも含む） */
export function isRecorded(id) {
  return !!entry(id);
}

/** いま解き直すべき（間違えたまま、まだ正解していない） */
export function needsRetry(id) {
  const e = entry(id);
  return !!e && e.w > e.c;
}

/**
 * 不正解を記録する。未登録なら何もしない。
 * @returns {boolean} 記録したか（UI の案内を出し分けるため）
 */
export function recordMiss(id) {
  if (!id || !canUseMissed()) return false;
  if (!progress.missed || typeof progress.missed !== "object") progress.missed = {};
  const e = entry(id) || { n: 0, w: 0, c: 0 };
  progress.missed[id] = { n: e.n + 1, w: Date.now(), c: e.c };
  save();
  return true;
}

/**
 * 正解したときに呼ぶ。間違えたまま残っていた問題なら「克服」として記録する。
 * @returns {{ cleared: boolean, misses: number }} cleared＝今回この問題を克服した
 */
export function clearMiss(id) {
  if (!id || !canUseMissed()) return { cleared: false, misses: 0 };
  const e = entry(id);
  if (!e) return { cleared: false, misses: 0 };
  if (e.w <= e.c) return { cleared: false, misses: e.n }; // すでに克服済み
  progress.missed[id] = { n: e.n, w: e.w, c: Date.now() };
  save();
  return { cleared: true, misses: e.n };
}

/** 記録そのものを消す（一覧の ✕） */
export function forget(id) {
  if (!id || !canUseMissed()) return;
  if (progress.missed?.[id]) {
    delete progress.missed[id];
    save();
  }
}

/** 克服済みの記録をまとめて消す */
export function forgetCleared() {
  if (!canUseMissed()) return 0;
  let n = 0;
  for (const p of clearedProblems()) {
    delete progress.missed[p.id];
    n++;
  }
  if (n) save();
  return n;
}

/**
 * 挑戦履歴から記録を組み立てる（ログインした直後に1度だけ呼ぶ）。
 * 履歴 progress.attempts は未登録でもローカルに残っているので、
 * 「登録した瞬間から記録が始まる」のではなく、
 * それまでに間違えた問題もそのまま一覧に出せる。
 * すでに記録がある問題には触らない（クラウドの記録を壊さないため）。
 * @returns {number} 新しく作った記録の数
 */
export function seedFromAttempts() {
  if (!canUseMissed()) return 0;
  if (!progress.missed || typeof progress.missed !== "object") progress.missed = {};
  let made = 0;
  for (const [id, list] of Object.entries(progress.attempts || {})) {
    if (!Array.isArray(list) || list.length === 0) continue;
    if (progress.missed[id]) continue; // 既存の記録が正
    let n = 0;
    let w = 0;
    let c = 0;
    for (const a of list) {
      if (!a || typeof a.t !== "number") continue;
      if (a.ok) c = Math.max(c, a.t);
      else {
        n++;
        w = Math.max(w, a.t);
      }
    }
    if (n === 0) continue; // 一度も間違えていない問題は記録しない
    progress.missed[id] = { n, w, c };
    made++;
  }
  if (made) save();
  return made;
}

/* ── 一覧 ─────────────────────────────────────────────── */

function listBy(filter) {
  return CURRICULUM.filter((p) => {
    const e = entry(p.id);
    return e && filter(e);
  }).map((p) => ({ ...p, miss: entry(p.id) }));
}

/**
 * 解き直すべき問題。誤答回数の多い順 → 最近間違えた順。
 * 「よく間違える問題を先に」が復習の効率としては素直な並び。
 */
export function missedProblems() {
  return listBy((e) => e.w > e.c).sort((a, b) => b.miss.n - a.miss.n || b.miss.w - a.miss.w);
}

/** 克服済みの問題（克服が新しい順） */
export function clearedProblems() {
  return listBy((e) => e.w <= e.c).sort((a, b) => b.miss.c - a.miss.c);
}

export function missedCount() {
  return missedProblems().length;
}

export function missedCountIn(trackId) {
  return missedProblems().filter((p) => p.track === trackId).length;
}

/* ── 連続で解き直す（永続化しない一時状態） ───────────────
   「▶ 解き直す」で作った順番を覚えておき、問題画面の
   「次の問題へ」がおまかせ出題ではなくこの順で進むようにする。
   ────────────────────────────────────────────────────── */

export const drill = $state({ queue: [], at: 0 });

/**
 * 解き直しを開始する。
 * @param {string|null} trackId 指定すればそのトラックだけ
 * @returns {string|null} 最初に開く問題 id
 */
export function startDrill(trackId = null) {
  const list = missedProblems().filter((p) => !trackId || p.track === trackId);
  drill.queue = list.map((p) => p.id);
  drill.at = 0;
  return drill.queue[0] || null;
}

export function endDrill() {
  drill.queue = [];
  drill.at = 0;
}

/** 解き直し中で、いま開いている問題が待ち行列のものか */
export function inDrill(id) {
  return drill.queue.length > 0 && drill.queue[drill.at] === id;
}

/** 残り問題数（いま開いているものを含む） */
export function drillLeft() {
  return Math.max(0, drill.queue.length - drill.at);
}

/**
 * 次の問題へ進む。すでに克服した問題は飛ばす。
 * @returns {string|null} 次に開く問題 id。無ければ null（＝やり切った）
 */
export function nextInDrill() {
  while (drill.at < drill.queue.length - 1) {
    drill.at++;
    const id = drill.queue[drill.at];
    if (problemById(id) && needsRetry(id)) return id;
  }
  endDrill();
  return null;
}
